import {
  useDataProvider,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { formatDayMonth } from "../funnel/cardStatus";
import { fullName, usePermissions, useRefreshFunnel } from "../funnel/hooks";
import { FunnelShell, NativeSelect } from "../funnel/shared";
import type { Apartment, ApartmentStatus, DealCard } from "../types";
import {
  AREA_FILTERS,
  matchesArea,
  parsePrice,
  type AreaFilter,
} from "./apartmentLogic";

const STATUSES: ApartmentStatus[] = ["free", "booked", "sold", "off"];

// docs/design/Apartments.dc.html cell styles
const statusClass: Record<ApartmentStatus, string> = {
  free: "bg-[#e3f1e8] border-[#8fc5a3] text-[#17703c] dark:bg-[#1c3a29] dark:border-[#3f7a55] dark:text-[#9bd4b0]",
  booked:
    "bg-[#fbefd2] border-[#d9a93f] border-dashed text-[#7a4f00] dark:bg-[#3d3014] dark:border-[#b8862a] dark:text-[#f0c66b]",
  sold: "bg-[#f3dcd8] border-[#d99b92] text-[#a82f23] dark:bg-[#3e1f1b] dark:border-[#8f4a41] dark:text-[#f0a79c]",
  off: "bg-[#e6e8e8] border-[#b8c0c0] text-[#5c6770] bg-[repeating-linear-gradient(135deg,transparent_0,transparent_5px,rgba(92,103,112,0.18)_5px,rgba(92,103,112,0.18)_6px)] dark:bg-[#2a2f30] dark:border-[#56606a] dark:text-[#aab4ba]",
};
// Phone: the section as a facade. Solid colours, sold flats are grey and silent
const mobileStatusClass: Record<ApartmentStatus, string> = {
  free: "bg-[#2f7a50] text-white dark:bg-[#3a8d5f]",
  booked: "bg-[#e3ac45] text-[#3d2a00]",
  sold: "bg-[#dcdfd6] text-transparent dark:bg-[#343a3b]",
  off: "bg-[#eceeee] text-transparent bg-[repeating-linear-gradient(135deg,transparent_0,transparent_4px,rgba(92,103,112,0.2)_4px,rgba(92,103,112,0.2)_5px)] dark:bg-[#2a2f30]",
};
const roomsStripe: Record<number, string> = {
  1: "shadow-[inset_5px_0_0_#9db4c8]",
  2: "shadow-[inset_5px_0_0_#5f84a6]",
  3: "shadow-[inset_5px_0_0_#2f4f73]",
};
const stripeColor: Record<number, string> = {
  1: "#9db4c8",
  2: "#5f84a6",
  3: "#2f4f73",
};

const area = (value: number | null, locale: string) =>
  value == null
    ? ""
    : Number(value).toLocaleString(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

/** Apartment chessboard (docs/design/Apartments, ApartmentsM) */
export const ApartmentsPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [locale] = useLocaleState();
  const { data: apartments = [], isPending } = useGetList<Apartment>(
    "apartments",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "number", order: "ASC" },
    },
  );
  // Who an apartment is held for: its active deal (RLS limits a manager to theirs)
  const { data: deals = [] } = useGetList<DealCard>("deal_cards", {
    filter: { "stage@neq": "lost", "archived_at@is": null },
    pagination: { page: 1, perPage: 2000 },
  });
  const dealOf = useMemo(() => {
    const map = new Map<Identifier, DealCard>();
    for (const deal of deals)
      if (deal.apartment_id != null) map.set(deal.apartment_id, deal);
    return map;
  }, [deals]);

  const sections = useMemo(
    () => [...new Set(apartments.map((a) => a.section))].sort((a, b) => a - b),
    [apartments],
  );
  const [section, setSection] = useState<number>();
  const current = section ?? sections[0];
  const [rooms, setRooms] = useState(0);
  const [areaFilter, setAreaFilter] = useState<AreaFilter>("any");
  const [selectedId, setSelectedId] = useState<Identifier>();
  const [onlyFree, setOnlyFree] = useState(false);

  const inSection = apartments.filter((a) => a.section === current);
  const floors = [...new Set(inSection.map((a) => a.floor))].sort(
    (a, b) => b - a,
  );
  const perFloor = Math.max(
    1,
    ...floors.map((f) => inSection.filter((a) => a.floor === f).length),
  );
  const roomOptions = [...new Set(inSection.map((a) => a.rooms))].sort();
  const counts = Object.fromEntries(
    STATUSES.map((s) => [s, inSection.filter((a) => a.status === s).length]),
  ) as Record<ApartmentStatus, number>;
  const selected = apartments.find((a) => a.id === selectedId);

  if (isPending) return null;

  if (apartments.length === 0) {
    return (
      <FunnelShell
        title={translate("resources.apartments.name", { smart_count: 2 })}
      >
        <p className="text-muted-foreground">
          {translate("crm.apartments.empty")}
        </p>
      </FunnelShell>
    );
  }

  const sectionTabs =
    sections.length > 1 ? (
      <div className="flex rounded-xl border bg-card p-1 text-sm">
        {sections.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={value === current}
            onClick={() => {
              setSection(value);
              setSelectedId(undefined);
            }}
            className={cn(
              "flex-1 rounded-lg px-4 py-1.5 font-semibold",
              value === current
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground",
            )}
          >
            {translate("crm.apartments.section", { section: value })}
          </button>
        ))}
      </div>
    ) : (
      <span className="font-semibold text-muted-foreground">
        {translate("crm.apartments.section", { section: current })}
      </span>
    );

  return (
    <FunnelShell
      title={translate("resources.apartments.name", { smart_count: 2 })}
    >
      {isMobile ? (
        <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none]">
          {sections.length > 1
            ? sections.map((value) => (
                <button
                  key={`s${value}`}
                  type="button"
                  aria-pressed={value === current}
                  onClick={() => {
                    setSection(value);
                    setSelectedId(undefined);
                  }}
                  className={cn(
                    "shrink-0 rounded-full border px-3 py-1 text-sm font-semibold whitespace-nowrap",
                    value === current
                      ? "border-[#2f5d46] bg-[#2f5d46] text-white"
                      : "bg-card text-muted-foreground",
                  )}
                >
                  {translate("crm.apartments.section_short", {
                    section: value,
                  })}
                </button>
              ))
            : null}
          {sections.length > 1 ? (
            <span className="mx-0.5 w-px shrink-0 self-stretch bg-border" />
          ) : null}
          {[0, ...roomOptions].map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={rooms === value}
              onClick={() => setRooms(value)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1 text-sm font-semibold whitespace-nowrap",
                rooms === value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground",
              )}
            >
              {value === 0
                ? translate("crm.apartments.all")
                : translate("crm.apartments.rooms_short", { count: value })}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={onlyFree}
            onClick={() => setOnlyFree((v) => !v)}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1 text-sm font-semibold whitespace-nowrap",
              onlyFree
                ? "border-[#2f7a50] bg-[#2f7a50] text-white"
                : "bg-card text-muted-foreground",
            )}
          >
            {translate("crm.apartments.only_free")}
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          isMobile
            ? // Phone: one card, the roof with counts on top of the facade
              "mt-3 flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm"
            : "flex items-stretch gap-6",
        )}
      >
        {isMobile ? (
          <div className="w-full">
            {/* roof: what is left to sell, the legend in one line */}
            <div className="flex items-center justify-around gap-2 bg-[#2f5d46] px-3 py-2 text-[13px] text-white">
              {(["free", "booked", "sold"] as const).map((status) => (
                <span key={status} className="inline-flex items-center gap-1">
                  <i
                    className={cn(
                      "inline-block size-2.5 rounded-sm",
                      status === "free"
                        ? "bg-[#7fcf9d]"
                        : status === "booked"
                          ? "bg-[#e3ac45]"
                          : "bg-[#dcdfd6]",
                    )}
                  />
                  {translate(`crm.apartments.counts.${status}`)}{" "}
                  <b>{counts[status]}</b>
                </span>
              ))}
            </div>
          </div>
        ) : null}
        <div
          className={cn(
            "grid min-w-0",
            isMobile ? "w-full gap-1 p-2" : "flex-1 gap-1.5",
          )}
          style={{
            gridTemplateColumns: `${isMobile ? 18 : 56}px repeat(${perFloor}, minmax(0, 1fr))`,
            // Desktop: the whole section fits the screen under the title
            ...(isMobile
              ? {}
              : {
                  height: "calc(100dvh - 7rem)",
                  minHeight: `${floors.length * 3}rem`,
                  gridTemplateRows: `repeat(${floors.length}, minmax(0, 1fr))`,
                }),
          }}
        >
          {floors.map((floor) => [
            <div
              key={`floor-${floor}`}
              className={cn(
                "flex flex-col justify-center font-bold text-muted-foreground",
                isMobile ? "items-center text-[11px]" : "text-xs",
              )}
            >
              <span>{floor}</span>
              {isMobile ? null : (
                <span>{translate("crm.apartments.floor")}</span>
              )}
            </div>,
            ...inSection
              .filter((a) => a.floor === floor)
              .sort((a, b) => a.number - b.number)
              .map((apartment) => {
                const visible =
                  (rooms === 0 || apartment.rooms === rooms) &&
                  matchesArea(apartment.total_area, areaFilter) &&
                  (!onlyFree || apartment.status === "free");
                const deal = dealOf.get(apartment.id);
                const until =
                  apartment.status === "booked" && apartment.booked_until
                    ? formatDayMonth(new Date(apartment.booked_until))
                    : null;
                return (
                  <button
                    key={apartment.id}
                    type="button"
                    onClick={() => setSelectedId(apartment.id)}
                    aria-pressed={apartment.id === selectedId}
                    aria-label={`${apartment.number}, ${translate(`resources.apartments.statuses.${apartment.status}`)}`}
                    title={[
                      translate(
                        `resources.apartments.statuses.${apartment.status}`,
                      ),
                      deal ? fullName(deal) : null,
                      until
                        ? translate("crm.apartments.until", { date: until })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    className={cn(
                      "flex flex-col items-center justify-center gap-0.5 border tabular-nums transition-opacity",
                      isMobile
                        ? "aspect-square rounded-md border-0"
                        : "min-h-0 rounded-lg px-1 py-1",
                      isMobile
                        ? mobileStatusClass[apartment.status]
                        : statusClass[apartment.status],
                      !isMobile && roomsStripe[apartment.rooms],
                      apartment.id === selectedId &&
                        "outline-2 outline-offset-1 outline-[#d89a2b]",
                      !visible && "opacity-20",
                    )}
                  >
                    <span
                      className={cn(
                        "leading-tight",
                        isMobile
                          ? "text-sm font-bold"
                          : "text-lg font-extrabold text-[#1f2a21] dark:text-white",
                      )}
                    >
                      {apartment.number}
                    </span>
                    {isMobile ? null : (
                      <span className="text-sm font-semibold">
                        {apartment.rooms}к ·{" "}
                        {area(apartment.total_area, locale)}
                      </span>
                    )}
                    {until && !isMobile ? (
                      <span className="text-xs font-bold">
                        {translate("crm.apartments.until", { date: until })}
                      </span>
                    ) : null}
                  </button>
                );
              }),
          ])}
        </div>

        {isMobile ? (
          <Drawer
            open={selected != null}
            onOpenChange={(open) => {
              if (!open) setSelectedId(undefined);
            }}
          >
            <DrawerContent>
              <DrawerTitle className="sr-only">
                {translate("resources.apartments.name", { smart_count: 1 })}
              </DrawerTitle>
              <div className="max-h-[75dvh] overflow-y-auto p-5 pb-8">
                {selected ? (
                  <ApartmentPanel
                    key={selected.id}
                    apartment={selected}
                    deal={dealOf.get(selected.id)}
                  />
                ) : null}
              </div>
            </DrawerContent>
          </Drawer>
        ) : (
          <>
            <aside className="flex w-[280px] flex-none flex-col gap-4 2xl:w-[320px]">
              {sectionTabs}
              <div className="grid grid-cols-2 gap-2">
                {STATUSES.map((status) => (
                  <div
                    key={status}
                    className={cn(
                      "flex flex-col rounded-xl border px-3 py-2 text-sm font-bold",
                      statusClass[status],
                    )}
                  >
                    <b className="text-2xl font-extrabold tabular-nums">
                      {counts[status]}
                    </b>
                    {translate(`crm.apartments.counts.${status}`)}
                  </div>
                ))}
              </div>
              <div
                role="group"
                aria-label={translate("resources.apartments.fields.rooms")}
                className="flex rounded-xl border bg-card p-1 text-sm"
              >
                {[0, ...roomOptions].map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={rooms === value}
                    onClick={() => setRooms(value)}
                    className={cn(
                      "flex-1 rounded-lg px-2 py-1.5 font-semibold whitespace-nowrap",
                      rooms === value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {value === 0
                      ? translate("crm.apartments.all")
                      : translate("crm.funnel.rooms", { count: value })}
                  </button>
                ))}
              </div>
              <label className="flex flex-col gap-1 text-sm font-semibold text-muted-foreground">
                {translate("crm.apartments.area")}
                <NativeSelect
                  value={areaFilter}
                  onChange={(event) =>
                    setAreaFilter(event.target.value as AreaFilter)
                  }
                >
                  {AREA_FILTERS.map((value) => (
                    <option key={value} value={value}>
                      {translate(`crm.apartments.area_filters.${value}`)}
                    </option>
                  ))}
                </NativeSelect>
              </label>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {roomOptions.map((value) => (
                  <span
                    key={value}
                    className="inline-flex items-center gap-1.5"
                  >
                    <i
                      className="inline-block h-3.5 w-[5px]"
                      style={{
                        backgroundColor: stripeColor[value] ?? "#2f4f73",
                      }}
                    />
                    {translate("crm.apartments.rooms_short", { count: value })}
                  </span>
                ))}
                <span className="font-semibold">
                  {translate("crm.apartments.stripe_hint")}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {translate("crm.apartments.pick")}
              </p>
            </aside>
            <Dialog
              open={selected != null}
              onOpenChange={(open) => {
                if (!open) setSelectedId(undefined);
              }}
            >
              <DialogContent className="sm:max-w-md">
                <DialogTitle className="sr-only">
                  {translate("resources.apartments.name", { smart_count: 1 })}
                </DialogTitle>
                {selected ? (
                  <ApartmentPanel
                    key={selected.id}
                    apartment={selected}
                    deal={dealOf.get(selected.id)}
                    onSaved={() => setSelectedId(undefined)}
                  />
                ) : null}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </FunnelShell>
  );
};

ApartmentsPage.path = "/apartments";

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

const ApartmentPanel = ({
  apartment,
  deal,
  onSaved,
}: {
  apartment: Apartment;
  deal: DealCard | undefined;
  onSaved?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefreshFunnel();
  const [locale] = useLocaleState();
  const canEdit = usePermissions().includes("apartments.edit");
  const [price, setPrice] = useState(
    apartment.price_per_m2 == null ? "" : String(apartment.price_per_m2),
  );
  const [status, setStatus] = useState<ApartmentStatus>(apartment.status);
  const [until, setUntil] = useState(toDateInput(apartment.booked_until));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (status !== "booked") setUntil("");
  }, [status]);
  const parsedPrice = parsePrice(price);
  const invalid =
    (status === "booked" && !until) ||
    (price.trim() !== "" && parsedPrice == null);

  const save = async () => {
    setSaving(true);
    try {
      await dataProvider.update("apartments", {
        id: apartment.id,
        data: {
          price_per_m2: parsedPrice,
          status,
          // End of the chosen day: the booking holds all of it
          booked_until:
            status === "booked" && until
              ? new Date(`${until}T23:59:59`).toISOString()
              : null,
        },
        previousData: apartment,
      });
      notify("crm.apartments.saved");
      await refresh();
      onSaved?.();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const total =
    apartment.total_area && parsedPrice
      ? Math.round(apartment.total_area * parsedPrice).toLocaleString(locale)
      : null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-extrabold">
          {translate("crm.funnel.lead.section_number", {
            section: apartment.section,
            number: apartment.number,
          })}
        </h2>
        <div className="text-sm text-muted-foreground">
          {translate("crm.funnel.lead.apartment_line", {
            floor: apartment.floor,
            rooms: apartment.rooms,
          })}
        </div>
      </div>
      <div className="flex gap-4 text-sm">
        <div className="flex flex-col">
          <span className="text-muted-foreground">
            {translate("crm.apartments.total")}
          </span>
          <b className="text-base">{area(apartment.total_area, locale)} м²</b>
        </div>
        <div className="flex flex-col">
          <span className="text-muted-foreground">
            {translate("crm.apartments.living")}
          </span>
          <b className="text-base">
            {apartment.living_area
              ? `${area(apartment.living_area, locale)} м²`
              : "—"}
          </b>
        </div>
      </div>
      {deal ? (
        <div className="text-sm">
          <span className="text-muted-foreground">
            {translate("crm.apartments.held_for")}{" "}
          </span>
          <Link
            to={`/funnel/${deal.id}`}
            className="font-bold text-primary underline"
          >
            {fullName(deal)}
          </Link>
        </div>
      ) : null}

      {canEdit ? (
        <>
          <label className="flex flex-col gap-1 text-xs font-bold text-muted-foreground">
            {translate("crm.apartments.price")}
            <Input
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder={translate("crm.apartments.no_price")}
            />
            {total ? (
              <span className="text-xl font-extrabold text-foreground">
                {translate("crm.apartments.total_price", { total })}
              </span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-muted-foreground">
            {translate("resources.apartments.fields.status")}
            <NativeSelect
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as ApartmentStatus)
              }
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {translate(`resources.apartments.statuses.${value}`)}
                </option>
              ))}
            </NativeSelect>
          </label>
          {status === "booked" ? (
            <label className="flex flex-col gap-1 text-xs font-bold text-muted-foreground">
              {translate("resources.apartments.fields.booked_until")}
              <Input
                type="date"
                value={until}
                onChange={(event) => setUntil(event.target.value)}
              />
            </label>
          ) : null}
          <Button
            type="button"
            size="lg"
            onClick={save}
            disabled={saving || invalid}
          >
            {translate("ra.action.save")}
          </Button>
        </>
      ) : (
        <div className="flex flex-col gap-1 text-sm">
          {total ? (
            <div className="text-xl font-extrabold">
              {translate("crm.apartments.total_price", { total })}
            </div>
          ) : null}
          <div>
            <span className="text-muted-foreground">
              {translate("crm.apartments.price")}:{" "}
            </span>
            <b>
              {apartment.price_per_m2 == null
                ? translate("crm.apartments.no_price")
                : Number(apartment.price_per_m2).toLocaleString(locale)}
            </b>
          </div>
          <div>
            <span className="text-muted-foreground">
              {translate("resources.apartments.fields.status")}:{" "}
            </span>
            <b>
              {translate(`resources.apartments.statuses.${apartment.status}`)}
            </b>
            {apartment.status === "booked" && apartment.booked_until
              ? ` ${translate("crm.apartments.until", {
                  date: formatDayMonth(new Date(apartment.booked_until)),
                })}`
              : null}
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {translate("crm.apartments.rights_hint")}
      </p>
    </div>
  );
};
